import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  FeatherPlus, 
  FeatherEdit2, 
  FeatherTrash2, 
  FeatherPhone, 
  FeatherMail, 
  FeatherGlobe, 
  FeatherClock,
  FeatherUser,
  FeatherCalendar
} from '../ui/icons';
import ContactAttemptForm from './ContactAttemptForm';
import api from '../../services/api';
import './ContactHistory.css';

const ContactHistory = ({ partner, onUpdate }) => {
  const [contactHistory, setContactHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAttempt, setEditingAttempt] = useState(null);
  const [deletingAttempt, setDeletingAttempt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (partner?.id) {
      fetchContactHistory();
    }
  }, [partner?.id]);

  const fetchContactHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/contact-attempts/partner/${partner.id}/history`);
      setContactHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching contact history:', error);
      setError('Failed to load contact history');
    } finally {
      setLoading(false);
    }
  };

  const handleContactAttemptSuccess = (attempt) => {
    setContactHistory(prev => [attempt, ...prev]);
    setShowForm(false);
    setEditingAttempt(null);
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleEditAttempt = (attempt) => {
    setEditingAttempt(attempt);
    setShowForm(true);
  };

  const handleDeleteAttempt = async (attemptId) => {
    if (!window.confirm('Are you sure you want to delete this contact attempt?')) {
      return;
    }

    try {
      setDeletingAttempt(attemptId);
      await api.delete(`/api/contact-attempts/${attemptId}`);

      setContactHistory(prev => prev.filter(attempt => attempt.id !== attemptId));
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting contact attempt:', error);
      setError('Failed to delete contact attempt');
    } finally {
      setDeletingAttempt(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'interested':
        return 'success';
      case 'phone_called':
      case 'email_sent':
      case 'sent':
        return 'neutral-secondary';
      case 'phone_no_answer':
      case 'email_bounced':
      case 'failed':
        return 'warning';
      case 'not_interested':
      case 'cancelled':
        return 'danger';
      default:
        return 'neutral-secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'failed':
        return 'Failed';
      case 'phone_called':
        return 'Phone Called';
      case 'phone_answered':
        return 'Phone Answered';
      case 'phone_no_answer':
        return 'No Answer';
      case 'phone_voicemail':
        return 'Voicemail';
      case 'email_sent':
        return 'Email Sent';
      case 'email_bounced':
        return 'Email Bounced';
      case 'email_opened':
        return 'Email Opened';
      case 'email_clicked':
        return 'Email Clicked';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'phone':
        return <FeatherPhone size={16} />;
      case 'email':
        return <FeatherMail size={16} />;
      case 'linkedin':
        return <FeatherGlobe size={16} />;
      case 'in_person':
        return <FeatherUser size={16} />;
      case 'video_call':
        return <FeatherPhone size={16} />;
      default:
        return <FeatherMail size={16} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="contact-history">
        <div className="contact-history-header">
          <h3>Contact History</h3>
        </div>
        <div className="loading">Loading contact history...</div>
      </div>
    );
  }

  return (
    <div className="contact-history">
      <div className="contact-history-header">
        <h3>Contact History</h3>
        <Button
          variant="brand-primary"
          size="sm"
          icon={<FeatherPlus />}
          onClick={() => setShowForm(true)}
        >
          Log Contact
        </Button>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <Button
            variant="neutral-tertiary"
            size="sm"
            onClick={() => setError('')}
          >
            Dismiss
          </Button>
        </div>
      )}

      {contactHistory.length === 0 ? (
        <div className="empty-state">
          <p>No contact attempts recorded yet.</p>
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={() => setShowForm(true)}
          >
            Log First Contact
          </Button>
        </div>
      ) : (
        <div className="contact-timeline">
          {contactHistory.map((attempt, index) => (
            <div key={attempt.id} className="contact-timeline-item">
              <div className="timeline-marker">
                <div className="timeline-icon">
                  {getMethodIcon(attempt.contact_method)}
                </div>
                <div className="timeline-line"></div>
              </div>
              
              <div className="timeline-content">
                <div className="attempt-header">
                  <div className="attempt-info">
                    <div className="attempt-meta">
                      <span className="attempt-number">Attempt #{attempt.attempt_number}</span>
                      <span className="attempt-date">{formatDate(attempt.attempt_date)}</span>
                      <span className="attempt-user">by {attempt.users?.display_name || 'Unknown'}</span>
                    </div>
                    <div className="attempt-stats">
                      <Badge variant="neutral-secondary" size="sm">
                        📞 {attempt.phone_attempts} calls
                      </Badge>
                      <Badge variant="neutral-secondary" size="sm">
                        📧 {attempt.email_attempts} emails
                      </Badge>
                      <Badge variant="neutral-secondary" size="sm">
                        Total: {attempt.total_attempts}
                      </Badge>
                      {attempt.has_follow_ups && (
                        <Badge variant="warning" size="sm">
                          🔄 {attempt.follow_up_count} follow-ups
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="attempt-status">
                    <Badge 
                      variant={getStatusColor(attempt.status)}
                      size="sm"
                    >
                      {getStatusLabel(attempt.status)}
                    </Badge>
                    {attempt.outcome && (
                      <Badge 
                        variant="neutral-secondary" 
                        size="sm"
                        style={{ marginLeft: '0.5rem' }}
                      >
                        {attempt.outcome}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="attempt-details">
                  {attempt.contact_person_name && (
                    <div className="contact-person">
                      <strong>Contact:</strong> {attempt.contact_person_name}
                      {attempt.contact_person_role && ` (${attempt.contact_person_role})`}
                    </div>
                  )}
                  
                  {attempt.subject && (
                    <div className="attempt-subject">
                      <strong>Subject:</strong> {attempt.subject}
                    </div>
                  )}
                  
                  <div className="attempt-content">
                    <strong>Details:</strong> {attempt.content}
                  </div>
                  
                  {attempt.notes && (
                    <div className="attempt-notes">
                      <strong>Notes:</strong> {attempt.notes}
                    </div>
                  )}
                  
                  {attempt.response_content && (
                    <div className="attempt-response">
                      <strong>Response:</strong> {attempt.response_content}
                      {attempt.response_received_at && (
                        <span className="response-date">
                          {' '}({formatDate(attempt.response_received_at)})
                        </span>
                      )}
                    </div>
                  )}
                  
                  {attempt.has_follow_ups && attempt.next_follow_up_date && (
                    <div className="follow-up-info">
                      <FeatherClock size={14} />
                      <strong>Next Follow-up:</strong> {formatDate(attempt.next_follow_up_date)}
                    </div>
                  )}
                  
                  {attempt.tags && attempt.tags.length > 0 && (
                    <div className="attempt-tags">
                      <strong>Tags:</strong>
                      {attempt.tags.map((tag, tagIndex) => (
                        <Badge key={tagIndex} variant="neutral-secondary" size="sm" style={{ marginLeft: '0.25rem' }}>
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="attempt-actions">
                  <Button
                    variant="neutral-tertiary"
                    onClick={() => handleEditAttempt(attempt)}
                  >
                    <FeatherEdit2 size={16} />
                  </Button>
                  <Button
                    variant="neutral-tertiary"
                    onClick={() => handleDeleteAttempt(attempt.id)}
                    disabled={deletingAttempt === attempt.id}
                  >
                    <FeatherTrash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ContactAttemptForm
          partner={partner}
          initialData={editingAttempt}
          onSuccess={handleContactAttemptSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingAttempt(null);
          }}
        />
      )}
    </div>
  );
};

export default ContactHistory; 